import { BarChart, BookOpen, Briefcase, Calendar, Camera, Code, Cpu, CreditCard, FileText, MessageSquare, Music, Shield, Sparkles, Star, Users, Zap } from 'lucide-react';
import React, { useState } from 'react';
import WaitlistModal from './WaitlistModal';

const TutoringPlatform = () => {
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  return (
    <div className="min-h-screen bg-white pt-20"> {/* Added pt-20 for navbar space */}

      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <div onClick={() => setWaitlistOpen(true)} className="inline-flex items-center space-x-2 bg-purple-100 rounded-full px-4 py-2 mb-6 cursor-pointer">
              <Sparkles className="h-4 w-4 text-purple-600" />
              <span className="text-sm text-purple-600 font-medium">Connect with experts</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-8 bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              Master Any Skill with Expert Guidance
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Connect instantly with world-class mentors for live, personalized learning sessions in any field.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 mb-12">
              <div className="flex items-center bg-purple-50 rounded-full px-4 py-2">
                <Users className="h-4 w-4 text-purple-600 mr-2" />
                <span className="text-sm text-purple-600">Live 1:1 Sessions</span>
              </div>
              <div className="flex items-center bg-purple-50 rounded-full px-4 py-2">
                <Zap className="h-4 w-4 text-purple-600 mr-2" />
                <span className="text-sm text-purple-600">Instant Matching</span>
              </div>
              <div className="flex items-center bg-purple-50 rounded-full px-4 py-2">
                <Star className="h-4 w-4 text-purple-600 mr-2" />
                <span className="text-sm text-purple-600">Verified Experts</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Skills */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-6xl mx-auto">
            {[
              { icon: <Music className="h-6 w-6" />, name: 'Classical Music' },
              { icon: <BookOpen className="h-6 w-6" />, name: 'Spanish' },
              { icon: <Camera className="h-6 w-6" />, name: 'Photography' },
              { icon: <Briefcase className="h-6 w-6" />, name: 'Executive Coach' },
              { icon: <Code className="h-6 w-6" />, name: 'Software Engineering' },
              { icon: <Cpu className="h-6 w-6" />, name: 'Robotics' }
            ].map((skill) => (
              <div key={skill.name} className="bg-gradient-to-br from-white to-purple-50 rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300 border border-purple-100">
                <div className="flex justify-center mb-3 text-purple-600">{skill.icon}</div>
                <span className="text-gray-800 font-medium">{skill.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-16 text-center">
              Platform Features
            </h2>
            <div className="grid md:grid-cols-2 gap-12">
              <div className="bg-gradient-to-br from-white to-purple-50 p-8 rounded-2xl shadow-lg border border-purple-100">
                <div className="text-purple-600 mb-6">
                  <Star className="h-8 w-8" />
                </div>
                <h3 className="text-2xl font-bold mb-6">For Experts</h3>
                <ul className="space-y-4 text-gray-600">
                  <li className="flex items-start">
                    <Users className="h-5 w-5 mr-3 text-purple-600 mt-1 flex-shrink-0" />
                    <span>Access a global network of engaged learners</span>
                  </li>
                  <li className="flex items-start">
                    <Briefcase className="h-5 w-5 mr-3 text-purple-600 mt-1 flex-shrink-0" />
                    <span>Generate direct income through expert sessions</span>
                  </li>
                  <li className="flex items-start">
                    <Shield className="h-5 w-5 mr-3 text-purple-600 mt-1 flex-shrink-0" />
                    <span>Secure, enterprise-grade video conferencing</span>
                  </li>
                  <li className="flex items-start">
                    <CreditCard className="h-5 w-5 mr-3 text-purple-600 mt-1 flex-shrink-0" />
                    <span>Automated payment tracking and management</span>
                  </li>
                  <li className="flex items-start">
                    <Zap className="h-5 w-5 mr-3 text-purple-600 mt-1 flex-shrink-0" />
                    <span>AI-powered course material generation</span>
                  </li>
                  <li className="flex items-start">
                    <BarChart className="h-5 w-5 mr-3 text-purple-600 mt-1 flex-shrink-0" />
                    <span>Comprehensive student progress analytics</span>
                  </li>
                  <li className="flex items-start">
                    <Calendar className="h-5 w-5 mr-3 text-purple-600 mt-1 flex-shrink-0" />
                    <span>Intelligent scheduling optimization</span>
                  </li>
                </ul>
              </div>
              <div className="bg-gradient-to-br from-white to-purple-50 p-8 rounded-2xl shadow-lg border border-purple-100">
                <div className="text-purple-600 mb-6">
                  <Users className="h-8 w-8" />
                </div>
                <h3 className="text-2xl font-bold mb-6">For Learners</h3>
                <ul className="space-y-4 text-gray-600">
                  <li className="flex items-start">
                    <Users className="h-5 w-5 mr-3 text-purple-600 mt-1 flex-shrink-0" />
                    <span>Choose between private or group learning sessions</span>
                  </li>
                  <li className="flex items-start">
                    <Calendar className="h-5 w-5 mr-3 text-purple-600 mt-1 flex-shrink-0" />
                    <span>Flexible scheduling across time zones</span>
                  </li>
                  <li className="flex items-start">
                    <Shield className="h-5 w-5 mr-3 text-purple-600 mt-1 flex-shrink-0" />
                    <span>Premium HD video learning environment</span>
                  </li>
                  <li className="flex items-start">
                    <FileText className="h-5 w-5 mr-3 text-purple-600 mt-1 flex-shrink-0" />
                    <span>Comprehensive multimedia learning resources</span>
                  </li>
                  <li className="flex items-start">
                    <BarChart className="h-5 w-5 mr-3 text-purple-600 mt-1 flex-shrink-0" />
                    <span>Detailed progress tracking and insights</span>
                  </li>
                  <li className="flex items-start">
                    <MessageSquare className="h-5 w-5 mr-3 text-purple-600 mt-1 flex-shrink-0" />
                    <span>Direct messaging with your expert</span>
                  </li>
                  <li className="flex items-start">
                    <BookOpen className="h-5 w-5 mr-3 text-purple-600 mt-1 flex-shrink-0" />
                    <span>AI-enhanced learning materials with expert guidance</span>
                  </li>
                  <li className="flex items-start">
                    <CreditCard className="h-5 w-5 mr-3 text-purple-600 mt-1 flex-shrink-0" />
                    <span>Transparent payment and booking system</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Courses */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Featured Learning Paths</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-white to-purple-50 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 border border-purple-100">
              <div className="text-purple-600 mb-4">
                <Music className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold mb-3">Classical Vocal</h3>
              <p className="text-gray-600">Master vocal techniques with professional coaches in live sessions.</p>
            </div>
            <div className="bg-gradient-to-br from-white to-purple-50 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 border border-purple-100">
              <div className="text-purple-600 mb-4">
                <Code className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold mb-3">Full-Stack Development</h3>
              <p className="text-gray-600">Build modern web applications with industry-leading engineers.</p>
            </div>
            <div className="bg-gradient-to-br from-white to-purple-50 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 border border-purple-100">
              <div className="text-purple-600 mb-4">
                <Camera className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold mb-3">Professional Photography</h3>
              <p className="text-gray-600">Master composition and lighting with expert photographers.</p>
            </div>
          </div>
        </div>
      </section>
      <WaitlistModal isOpen={waitlistOpen} onClose={() => setWaitlistOpen(false)} />  
    </div>
  );
};

export default TutoringPlatform;
